import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { RootState } from 'store';
import Icon from 'components/ui/icon';
import { SideBarMenuItem } from '../SideBarMenuItem';
import SlideDown from 'components/ui/slideDown';
import TooltipInfo from 'components/ui/tooltipInfo';
import Utils from 'helpers/Utils';
import Link from 'components/ui/link';
import './sideBarSubMenu.scss';

interface Props {
  title: string;
  icon: string;
  children: {
    path: string;
    icon: string;
    title: string;
  }[];
}

export const SideBarSubMenu: FC<Props> = ({ title, icon, children }) => {
  const location = useLocation();
  const isMenuExpanded = useSelector(
    (state: RootState) => state.layoutConfiguration.isMenuExpanded
  );
  const isSubMenuInitiallyOpen =
    isMenuExpanded &&
    children.some(({ path }) => location.pathname.includes(path));
  const [isOpen, setOpen] = useState(isSubMenuInitiallyOpen ?? false);
  const tooltipId = useRef(Utils.getHash());

  useEffect(() => {
    if (!isMenuExpanded && isOpen) {
      setOpen(false);
    }
  }, [isMenuExpanded, isOpen]);

  const handleToogleSubMenu = () => {
    setOpen((prevState) => !prevState);
  };

  const subMenuList = useMemo(() => {
    return children.map(({ icon, title, path }) => {
      const selected = location.pathname.includes(path);

      return (
        <li key={path} className='sidebar-menu__list-item'>
          <Link url={path}>
            <SideBarMenuItem title={title} icon={icon} selected={selected} />
          </Link>
        </li>
      );
    });
  }, [location.pathname]);

  return (
    <div
      data-for={tooltipId.current}
      data-tip={!isMenuExpanded ? title : undefined}
      className={classnames('sidebar-menu-sub-menu', {
        'sidebar-menu-sub-menu_opened': isOpen,
      })}>
      <div
        className='sidebar-menu-sub-menu__item'
        onClick={isMenuExpanded ? handleToogleSubMenu : undefined}>
        <SideBarMenuItem
          title={title}
          icon={icon}
          active={children.some(({ path }) => location.pathname.includes(path))}
        />
        <Icon
          size={10}
          className='sidebar-menu-sub-menu__icon'
          name='im-Arrow-down'
        />
      </div>
      {!isMenuExpanded ? (
        <div className='sidebar-menu-sub-menu__tooltip-divider' />
      ) : null}

      <SlideDown
        isOpen={isOpen}
        keepMounted
        className='sidebar-menu-sub-menu__slide-menu'>
        <ul className='sidebar-menu-sub-menu__list'>{subMenuList}</ul>
      </SlideDown>

      {!isMenuExpanded ? (
        <TooltipInfo
          id={tooltipId.current}
          place='right'
          delayHide={200}
          className='sidebar-menu-sub-menu__tooltip'
          overridePosition={({ left, top }, event, target, ref) => ({
            left,
            top: top - 20 + ref!.clientHeight / 2,
          })}>
          <ul className='sidebar-menu-sub-menu__list'>
            <li className='sidebar-menu__list-item'>
              <SideBarMenuItem title={title} simple />
            </li>
            {subMenuList}
          </ul>
        </TooltipInfo>
      ) : null}
    </div>
  );
};
